import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CloudOff from '@mui/icons-material/CloudOff';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import LightModeIcon from '@mui/icons-material/LightMode';
import PersonIcon from '@mui/icons-material/Person';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import VpnKeyOutlined from '@mui/icons-material/VpnKeyOutlined';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { actions } from '../../actions';
import { useAuth } from '../../auth/AuthContext';

const AUTH_METHOD_DISPLAY = {
    bifrost: {
        icon: ShieldOutlined,
        color: 'primary.main',
        tooltip: 'Authenticated via Bifrost',
    },
    grace: {
        icon: CloudOff,
        color: 'warning.main',
        tooltip: 'Offline mode — Bifrost unavailable',
    },
    local: {
        icon: VpnKeyOutlined,
        color: 'success.main',
        tooltip: 'Mimir authentication',
    },
};

function UserMenuInner({ lightOn, toggleLight, dbVersion, cvsVersion }) {
    const { user, logout } = useAuth();
    const userName = user?.username || '';
    const isAdmin = Boolean(user?.staff);
    const authMethod = user?.authMethod || 'bifrost';
    const authDisplay = AUTH_METHOD_DISPLAY[authMethod];
    if (!authDisplay && typeof console !== 'undefined') {
        console.warn('[Mimir auth] Unknown authMethod "%s" — falling back to bifrost display', authMethod);
    }
    const resolvedDisplay = authDisplay || AUTH_METHOD_DISPLAY.bifrost;
    const AuthIcon = resolvedDisplay.icon;

    const groups = Array.isArray(user?.groups) ? user.groups : [];
    // Show the Grafana link when the user carries any Keycloak "grafana*" role
    // (surfaced as a group by the backend OIDC sync — Grafana Admin/Editor/Viewer).
    const hasGrafanaAccess = groups.some((group) => String(group).toLowerCase().startsWith('grafana'));

    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleToggleLight = () => {
        toggleLight();
        handleClose();
    };

    const handleLogout = () => {
        handleClose();
        void logout();
    };

    return (
        <>
            <Tooltip title={userName || 'Account'} arrow>
                <IconButton onClick={handleOpen} size='small' sx={{ ml: 0.5 }} data-testid='UserMenuAvatar'>
                    <Avatar
                        sx={(theme) => ({
                            width: 30,
                            height: 30,
                            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                            color: theme.palette.common.white,
                            transition: 'box-shadow 0.2s ease, transform 0.15s ease',
                            '&:hover': {
                                boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}`,
                                transform: 'scale(1.05)',
                            },
                        })}
                    >
                        <PersonIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={(e) => e.stopPropagation()}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        minWidth: 230,
                        mt: 1,
                        overflow: 'visible',
                        border: '1px solid',
                        borderColor: 'divider',
                    },
                }}
            >
                {/* User info header */}
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                        <Typography variant='subtitle2' noWrap fontWeight={600} sx={{ minWidth: 0 }}>
                            {userName || 'User'}
                        </Typography>
                        <Tooltip title={resolvedDisplay.tooltip} arrow>
                            <AuthIcon
                                data-testid={`auth-method-icon-${authMethod}`}
                                sx={{ fontSize: 16, color: resolvedDisplay.color }}
                            />
                        </Tooltip>
                    </Box>
                    <Typography variant='caption' color='text.secondary' noWrap display='block'>
                        {`DB v${dbVersion || '…'}  ·  CVS v${cvsVersion || '…'}`}
                    </Typography>
                </Box>

                <Divider />

                {/* Dark / light mode */}
                <MenuItem onClick={handleToggleLight} sx={{ gap: 1 }} data-testid='UserMenuThemeToggle'>
                    <ListItemIcon>
                        {lightOn ? <LightModeIcon fontSize='small' /> : <DarkModeIcon fontSize='small' />}
                    </ListItemIcon>
                    <ListItemText primary={lightOn ? 'Light mode' : 'Dark mode'} />
                    <Switch checked={lightOn} size='small' edge='end' disableRipple sx={{ pointerEvents: 'none' }} />
                </MenuItem>

                {/* Groups with tooltip */}
                {groups.length > 0 && (
                    <Tooltip
                        title={
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 0.5 }}>
                                {groups.map((group) => (
                                    <Chip
                                        key={group}
                                        label={group}
                                        size='small'
                                        variant='outlined'
                                        sx={(theme) => ({
                                            color: 'inherit',
                                            borderColor: alpha(theme.palette.common.white, 0.3),
                                            fontSize: 12,
                                        })}
                                    />
                                ))}
                            </Box>
                        }
                        placement='left'
                        arrow
                        enterDelay={200}
                        leaveDelay={150}
                        slotProps={{
                            tooltip: {
                                sx: { maxWidth: 320, py: 1, px: 1 },
                            },
                        }}
                    >
                        <MenuItem sx={{ gap: 1 }} data-testid='UserMenuGroups'>
                            <ListItemIcon>
                                <GroupsOutlined fontSize='small' />
                            </ListItemIcon>
                            <ListItemText primary='Groups' />
                            <Badge
                                badgeContent={groups.length}
                                color='primary'
                                sx={{
                                    ml: 1,
                                    '& .MuiBadge-badge': {
                                        fontSize: 11,
                                        height: 18,
                                        minWidth: 18,
                                    },
                                }}
                            />
                        </MenuItem>
                    </Tooltip>
                )}

                {/* Admin panel (staff only) */}
                {isAdmin && (
                    <MenuItem
                        component='a'
                        href={axios.defaults.baseURL + (authMethod === 'local' ? '/admin/local-login/' : '/admin/')}
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={handleClose}
                    >
                        <ListItemIcon>
                            <AdminPanelSettingsIcon fontSize='small' />
                        </ListItemIcon>
                        <ListItemText primary='Admin panel' />
                    </MenuItem>
                )}

                {/* Grafana (users with any grafana* Keycloak role) */}
                {hasGrafanaAccess && (
                    <MenuItem
                        component='a'
                        href={axios.defaults.baseURL + '/grafana'}
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={handleClose}
                        data-testid='UserMenuGrafana'
                    >
                        <ListItemIcon>
                            <InsightsIcon fontSize='small' />
                        </ListItemIcon>
                        <ListItemText primary='Grafana' />
                    </MenuItem>
                )}

                <Divider />

                {/* Logout */}
                <MenuItem onClick={handleLogout} data-testid='UserMenuLogout'>
                    <ListItemIcon>
                        <ExitToAppIcon fontSize='small' />
                    </ListItemIcon>
                    <ListItemText primary='Sign out' />
                </MenuItem>
            </Menu>
        </>
    );
}

UserMenuInner.propTypes = {
    lightOn: PropTypes.bool.isRequired,
    toggleLight: PropTypes.func.isRequired,
    dbVersion: PropTypes.string,
    cvsVersion: PropTypes.string,
};

function UserMenu(props) {
    return <UserMenuInner {...props} />;
}

const mapStateToProps = (state) => ({
    lightOn: state.preference.lightOn,
    dbVersion: state.health.dbVersion || '…',
    cvsVersion: state.health.cvsVersion || '…',
});

const mapDispatchToProps = (dispatch) => ({
    toggleLight: () => dispatch(actions.preference.toggleLight()),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserMenu);
